<template>
  <div>
    <Head title="Licenses - LEP1 Generation Capacity" />
    <section class="mt-2">
      <div v-if="copyNotice" class="cell-copy-notice" role="status" aria-live="polite">{{ copyNotice }}</div>
      <PageHeader
        title="Senarai Penjanaan Kapasiti LEP1"
        icon="issues"
        description="View LEP1 licenses, installation details and registered generation capacity."
      >
        <template #actions>
          <button type="button" class="px-4 py-2 rounded bg-indigo-600 text-white disabled:opacity-50" :disabled="metabaseLoading" @click="loadMetabase">
            <span v-if="metabaseLoading" class="loading-button-label">Loading <span class="loading-dots" aria-hidden="true"><span></span><span></span><span></span></span></span><span v-else>Refresh Data</span>
          </button>
        </template>
      </PageHeader>
      <div class="license-summary" aria-label="License summary" :aria-busy="metabaseLoading">
        <article v-for="card in summaryCards" :key="card.label" class="license-summary-card">
          <div><p class="license-summary-label">{{ card.label }}</p><p class="license-summary-value"><span v-if="metabaseLoading" role="status" aria-label="Loading"><span class="loading-dots" aria-hidden="true"><span></span><span></span><span></span></span></span><span v-else>{{ card.value }}</span></p><p class="license-summary-note">{{ card.note }}</p></div>
          <span class="license-summary-icon" aria-hidden="true">{{ card.icon }}</span>
        </article>
      </div>
      <p class="license-summary-caption">Summary reflects all matching search results, across every page.</p>
      <div v-if="metabaseError" class="p-4 mb-4 bg-red-100 text-red-800 rounded" role="alert">{{ metabaseError }}</div>
      <div class="license-filters mb-4">
        <div class="license-filter-grid">
          <input v-model.trim="metabaseSearch" type="search" placeholder="Search license number, email, name or reference..." aria-label="Search license records" class="form-input w-full" />
          <select v-model="metabaseSearchField" aria-label="Search field" class="form-select w-full">
            <option value="">All License Fields</option>
            <option v-for="field in licenseSearchFields" :key="field" :value="field">{{ field }}</option>
          </select>
          <select v-model="selectedTown" aria-label="Filter by Bandar Pepasangan" class="form-select w-full">
            <option value="">All Bandar Pepasangan</option>
            <option v-for="town in townOptions" :key="town.key" :value="town.key">{{ town.label }}</option>
          </select>
          <select v-model="selectedMedium" aria-label="Filter by Medium Penjanaan" class="form-select w-full">
            <option value="">All Medium Penjanaan</option>
            <option v-for="medium in mediumOptions" :key="medium.key" :value="medium.key">{{ medium.label }}</option>
          </select>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3 mt-3">
          <span class="text-sm text-gray-600">{{ filteredMetabaseRows.length.toLocaleString() }} of {{ metabaseRows.length.toLocaleString() }} records</span>
          <div class="flex flex-wrap gap-3">
            <button v-if="selectedTown" type="button" class="town-filter-clear" @click="selectedTown = ''">Clear town filter ×</button>
            <button v-if="selectedMedium" type="button" class="town-filter-clear" @click="selectedMedium = ''">Clear medium filter ×</button>
          </div>
        </div>
      </div>
      <div
        ref="scrollContainer"
        class="license-table-scroll bg-white rounded-md shadow overflow-x-auto"
        :class="{ 'is-dragging': tableDragging }"
        @pointerdown="startTableDrag"
        @pointermove="moveTableDrag"
        @pointerup="stopTableDrag"
        @pointercancel="stopTableDrag"
        @lostpointercapture="stopTableDrag"
        @dragstart.prevent
      >
        <table class="w-full whitespace-nowrap text-sm">
          <thead>
            <tr class="text-left bg-gray-100">
              <th v-for="column in metabaseColumns" :key="column" class="px-4 py-3 font-bold">{{ column }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(record, index) in paginatedMetabaseRows" :key="metabasePage + '-' + index" class="hover:bg-gray-50">
              <td v-for="column in metabaseColumns" :key="column" class="border-t px-4 py-3 copyable-cell" title="Double-click to copy" @dblclick.stop.prevent="copyCell(record[column])">
                <button v-if="isTownColumn(column) && townKey(record[column])" type="button" class="town-pill" :class="{ 'is-active': selectedTown === townKey(record[column]) }" :aria-pressed="selectedTown === townKey(record[column])" title="Click to toggle town filter; double-click to copy" @pointerdown.stop @click.stop="toggleTown(record[column])">{{ record[column] }}</button>
                <button v-else-if="isMediumColumn(column) && townKey(record[column])" type="button" class="town-pill" :class="{ 'is-active': selectedMedium === townKey(record[column]) }" :aria-pressed="selectedMedium === townKey(record[column])" title="Click to toggle generation medium filter; double-click to copy" @pointerdown.stop @click.stop="toggleMedium(record[column])">{{ record[column] }}</button>
                <template v-else>{{ record[column] == null ? '' : record[column] }}</template>
              </td>
            </tr>
            <tr v-if="!metabaseLoading && paginatedMetabaseRows.length === 0">
              <td :colspan="metabaseColumns.length || 1" class="px-4 py-6 text-center text-gray-500">No records found.</td>
            </tr>
            <tr v-if="metabaseLoading">
              <td :colspan="metabaseColumns.length || 1" class="px-4 py-6 text-center text-gray-500">
                <div role="status" aria-live="polite" class="flex flex-col items-center justify-center">
                  <span class="metabase-spinner" aria-hidden="true"></span>
                  <span>Loading Metabase records...</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex flex-wrap items-center justify-between mt-4 gap-3">
        <span class="text-sm text-gray-600">Page {{ metabasePage }} of {{ metabaseTotalPages }}</span>
        <div class="flex gap-2">
          <button type="button" class="px-3 py-2 border rounded disabled:opacity-40" :disabled="metabasePage <= 1" @click="metabasePage--">Previous</button>
          <button type="button" class="px-3 py-2 border rounded disabled:opacity-40" :disabled="metabasePage >= metabaseTotalPages" @click="metabasePage++">Next</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import PageHeader from '@/Shared/LicensePageHeader'

export default {
  components: { Head, PageHeader },
  layout: Layout,
  data() {
    return {
      copyNotice: '',
      copyNoticeTimer: null,
      tableLastDrag: 0,
      tableDragging: false,
      tableDragStartX: 0,
      tableDragScrollLeft: 0,
      tableDragPointerId: null,
      metabaseLoaded: false,
      metabaseRows: [],
      metabaseColumns: [],
      metabaseLoading: false,
      metabaseError: null,
      metabaseSearch: '',
      metabaseSearchField: '',
      selectedTown: '',
      selectedMedium: '',
      licenseSearchFields: ['No Lesen', 'Email Pemunya', 'Email Pencipta', 'Nama Pepasangan', 'Nama Pemunya', 'No Rujukan'],
      metabasePage: 1,
      metabasePageSize: 25,
    }
  },
  computed: {
    licenseSummary() {
      const column = name => this.metabaseColumns.find(key => key.trim().toUpperCase() === name)
      const licenseKey = column('NO LESEN')
      const statusKey = column('STATUS')
      const capacityKey = column('KAPASITI(KW)')
      const feeKey = column('FI(RM)')
      const applicationKey = column('APPLICATION ID')
      const referenceKey = column('NO RUJUKAN')
      const feeApplications = new Set()
      let fees = 0
      const licenses = new Set()
      const active = new Set()
      let capacity = 0
      let capacityCount = 0
      this.filteredMetabaseRows.forEach(row => {
        const license = licenseKey && row[licenseKey] != null ? String(row[licenseKey]).trim().toUpperCase() : ''
        if (license) {
          licenses.add(license)
          if (statusKey && String(row[statusKey]).trim().toLowerCase() === 'valid') active.add(license)
        }
        const application = applicationKey && row[applicationKey] != null ? String(row[applicationKey]).trim() : ''
        const reference = referenceKey && row[referenceKey] != null ? String(row[referenceKey]).trim().toUpperCase() : ''
        const feeIdentity = application ? 'application:' + application : reference ? 'reference:' + reference : license ? 'license:' + license : ''
        const fee = feeKey ? this.capacityNumber(row[feeKey]) : null
        if (feeIdentity && fee !== null && !feeApplications.has(feeIdentity)) {
          feeApplications.add(feeIdentity)
          fees += fee
        }
        const value = capacityKey ? this.capacityNumber(row[capacityKey]) : null
        if (value !== null) { capacity += value; capacityCount++ }
      })
      return { licenses: licenseKey ? licenses.size : null, active: licenseKey && statusKey ? active.size : null, capacity: capacityCount ? capacity : null, capacityCount, fees: feeKey ? (feeApplications.size || !this.filteredMetabaseRows.length ? fees : null) : null, feeCount: feeApplications.size }
    },
    summaryCards() {
      const stats = this.licenseSummary
      const available = this.metabaseLoaded && !this.metabaseError
      const number = value => available && value !== null ? value.toLocaleString('en-MY') : '—'
      return [
        { label: 'Total Licenses', value: number(stats.licenses), note: 'Unique license numbers', icon: '▤' },
        { label: 'Active Licenses', value: number(stats.active), note: 'Unique licenses with a Valid record', icon: '✓' },
        { label: 'Generation Capacity', value: available && stats.capacity !== null ? stats.capacity.toLocaleString('en-MY', { maximumFractionDigits: 2 }) + ' kW' : '—', note: stats.capacityCount + ' rows with numeric capacity', icon: 'ϟ' },
        { label: 'Total License Fees (RM)', value: available && stats.fees !== null ? stats.fees.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—', note: stats.feeCount + ' unique applications with recorded fees', icon: 'RM' },
      ]
    },
    townOptions() {
      const towns = new Map()
      this.metabaseRows.forEach(record => {
        Object.keys(record).filter(this.isTownColumn).forEach(column => {
          const key = this.townKey(record[column])
          if (key && !towns.has(key)) towns.set(key, String(record[column]).trim())
        })
      })
      return Array.from(towns, ([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label))
    },
    mediumOptions() {
      const mediums = new Map()
      this.metabaseRows.forEach(record => {
        Object.keys(record).forEach(column => {
          if (column.trim().toLowerCase() !== 'medium penjanaan') return
          const key = this.townKey(record[column])
          if (key && !mediums.has(key)) mediums.set(key, String(record[column]).trim())
        })
      })
      return Array.from(mediums, ([key, label]) => ({ key, label })).sort((a, b) => a.label.localeCompare(b.label))
    },
    filteredMetabaseRows() {
      const search = this.metabaseSearch.toLocaleLowerCase()
      return this.metabaseRows.filter(record => {
        const matchesTown = !this.selectedTown || Object.keys(record).some(column =>
          this.isTownColumn(column) && this.townKey(record[column]) === this.selectedTown
        )
        const matchesMedium = !this.selectedMedium || Object.keys(record).some(column =>
          column.trim().toLowerCase() === 'medium penjanaan' && this.townKey(record[column]) === this.selectedMedium
        )
        return matchesTown && matchesMedium && (!search || Object.keys(record).some(column => {
          const normalized = column.trim().toLocaleLowerCase()
          const fields = this.metabaseSearchField ? [this.metabaseSearchField] : this.licenseSearchFields
          return fields.some(field => field.toLocaleLowerCase() === normalized) &&
            String(record[column] == null ? '' : record[column]).toLocaleLowerCase().includes(search)
        }))
      })
    },
    metabaseTotalPages() {
      return Math.max(1, Math.ceil(this.filteredMetabaseRows.length / this.metabasePageSize))
    },
    paginatedMetabaseRows() {
      const start = (this.metabasePage - 1) * this.metabasePageSize
      return this.filteredMetabaseRows.slice(start, start + this.metabasePageSize)
    },
  },
  watch: {
    metabaseSearch() { this.metabasePage = 1 },
    metabaseSearchField() { this.metabasePage = 1 },
    selectedTown() { this.metabasePage = 1 },
    selectedMedium() { this.metabasePage = 1 },
  },
  mounted() {
    this.loadMetabase()
  },
  beforeUnmount() {
    clearTimeout(this.copyNoticeTimer)
  },
  methods: {
    isMediumColumn(column) { return column.trim().toLowerCase() === 'medium penjanaan' },
    toggleMedium(value) {
      const key = this.townKey(value)
      this.selectedMedium = this.selectedMedium === key ? '' : key
    },
    isTownColumn(column) { return column.trim().toLowerCase() === 'bandar pepasangan' },
    townKey(value) { return value == null ? '' : String(value).trim().toLocaleLowerCase() },
    toggleTown(value) {
      const key = this.townKey(value)
      this.selectedTown = this.selectedTown === key ? '' : key
    },
    async copyCell(value) {
      if (Date.now() - this.tableLastDrag < 300) return
      const text = value == null ? '' : String(value)
      let copied = false
      if (window.isSecureContext && navigator.clipboard && navigator.clipboard.writeText) {
        try { await navigator.clipboard.writeText(text); copied = true } catch (error) { /* Try local HTTP fallback below. */ }
      }
      if (!copied) {
        // Clipboard API is unavailable on ordinary HTTP local domains.
        const previousFocus = document.activeElement
        const field = document.createElement('textarea')
        field.value = text
        field.setAttribute('readonly', '')
        field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;'
        document.body.appendChild(field)
        try {
          field.focus({ preventScroll: true })
          field.select()
          copied = document.execCommand('copy')
        } catch (error) { copied = false }
        finally {
          field.remove()
          if (previousFocus && previousFocus.focus) previousFocus.focus({ preventScroll: true })
        }
      }
      this.copyNotice = copied ? 'Cell value copied!' : 'Copy blocked by browser. Select the text and press Ctrl+C.'
      clearTimeout(this.copyNoticeTimer)
      this.copyNoticeTimer = setTimeout(() => { this.copyNotice = '' }, 2400)
    },
    capacityNumber(value) {
      if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null
      if (typeof value !== 'string' || !value.trim()) return null
      const text = value.trim()
      // Accept decimal values and conventional comma thousands separators only.
      if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) return null
      const number = Number(text.replace(/,/g, ''))
      return Number.isFinite(number) ? number : null
    },
    startTableDrag(event) {
      // Keep native touch scrolling and interactive controls available.
      if (event.pointerType !== 'mouse' || event.button !== 0) return
      if (event.target.closest('a, button, input, select, textarea, [contenteditable]')) return
      const container = this.$refs.scrollContainer
      if (container.scrollWidth <= container.clientWidth) return
      this.tableDragging = false
      this.tableDragStartX = event.clientX
      this.tableDragScrollLeft = container.scrollLeft
      this.tableDragPointerId = event.pointerId
    },
    moveTableDrag(event) {
      if (event.pointerId !== this.tableDragPointerId) return
      if (!(event.buttons & 1)) { this.stopTableDrag(event); return }
      const container = this.$refs.scrollContainer
      if (!this.tableDragging) {
        if (Math.abs(event.clientX - this.tableDragStartX) < 5) return
        this.tableDragging = true
        container.setPointerCapture(event.pointerId)
      }
      event.preventDefault()
      this.$refs.scrollContainer.scrollLeft = this.tableDragScrollLeft
        - (event.clientX - this.tableDragStartX) * 2
    },
    stopTableDrag(event) {
      if (event.pointerId !== this.tableDragPointerId) return
      const container = this.$refs.scrollContainer
      if (this.tableDragging) this.tableLastDrag = Date.now()
      this.tableDragging = false
      this.tableDragPointerId = null
      if (container && container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId)
      }
    },
    async loadMetabase() {
      this.metabaseLoading = true
      this.metabaseError = null
      try {
        const response = await fetch('/estat/metabase-data', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })
        if (!response.ok) throw new Error('HTTP ' + response.status)
        const payload = await response.json()
        if (!payload.success || !Array.isArray(payload.data)) {
          throw new Error('Unexpected Metabase response')
        }
        this.metabaseLoaded = true
        this.metabaseRows = payload.data
        this.metabaseColumns = payload.data.length ? Object.keys(payload.data[0]) : []
        this.metabasePage = 1
      } catch (error) {
        this.metabaseError = 'Unable to load Metabase data: ' + error.message
      } finally {
        this.metabaseLoading = false
      }
    },
  },
}
</script>
<style scoped>
.license-filter-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px 18px; max-width: 900px; }
.license-filter-grid > * { min-width: 0; margin: 0; }
@media (max-width: 640px) { .license-filter-grid { grid-template-columns: minmax(0, 1fr); } }

.town-pill { display: inline-flex; align-items: center; padding: 6px 12px; border: 1px solid #b4dce5; border-radius: 999px; background: #edf8fa; color: #006483; font: inherit; cursor: pointer; }
.town-pill:hover { background: #d8eff4; }
.town-pill.is-active { background: #006483; border-color: #006483; color: white; }
.town-pill:focus-visible, .town-filter-clear:focus-visible { outline: 2px solid #006483; outline-offset: 3px; }
.town-filter-clear { color: #006483; padding: 8px; font-size: 14px; }

.copyable-cell:hover { background: #e6f3f7; }
.cell-copy-notice { position: fixed; right: 24px; bottom: 24px; z-index: 100000; max-width: calc(100vw - 48px); padding: 12px 20px; background: #006483; color: white; border-radius: 10px; box-shadow: 0 8px 24px rgb(0 0 0 / 15%); font-size: 13px; }

.license-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px; margin: 0 0 12px; }
.license-summary-card { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; background: #fff; padding: 22px; border: 1px solid #edf0f4; border-radius: 16px; box-shadow: 0 8px 22px rgb(0 100 131 / 5%); }
.license-summary-label { font-size: 11px; text-transform: uppercase; letter-spacing: .6px; color: #67748e; font-weight: 700; margin-bottom: 8px; }
.license-summary-value { font-size: 25px; font-weight: 700; line-height: 1.25; color: #006483; overflow-wrap: anywhere; }
.license-summary-note { font-size: 11px; color: #67748e; margin-top: 9px; line-height: 1.5; }
.license-summary-icon { display: grid; place-items: center; flex-shrink: 0; width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, #006483, #008aab); color: white; font-weight: 700; font-size: 20px; }
.license-summary-caption { font-size: 11px; color: #67748e; margin-bottom: 22px; }
@media (max-width: 1200px) { .license-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) { .license-summary { grid-template-columns: 1fr; } }

.license-table-scroll {
  cursor: grab;
}

.license-table-scroll.is-dragging,
.license-table-scroll.is-dragging * {
  cursor: grabbing;
  user-select: none;
}

.metabase-spinner {
  display: block;
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  border: 3px solid #e5e7eb;
  border-top-color: #006483;
  border-radius: 50%;
  animation: metabase-spin 0.8s linear infinite;
}

@keyframes metabase-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .metabase-spinner { animation: none; }
}
/* Three staggered CSS dots; colors follow their surrounding text. */
.loading-button-label { display:inline-flex;align-items:center;gap:8px; }
.loading-dots { display:inline-flex;align-items:center;gap:5px;height:1em;vertical-align:middle;padding:0 2px; }
.loading-dots > span { display:block;width:6px;height:6px;flex:0 0 6px;border-radius:50%;background:currentColor;animation:estat-dot-bounce 1.05s ease-in-out infinite; }
.loading-dots > span:nth-child(2) { animation-delay:.15s; }
.loading-dots > span:nth-child(3) { animation-delay:.3s; }
@keyframes estat-dot-bounce { 0%,60%,100% { transform:translateY(0);opacity:.55; } 30% { transform:translateY(-6px);opacity:1; } }
@media(prefers-reduced-motion:reduce) { .loading-dots > span { animation:none;opacity:1; } }
</style>